import React, {
  useCallback,
  useRef,
  useMemo,
  useEffect,
  useState,
} from "react";
import styles from "./FullScreenWrapper.module.scss";
import FSIcon from "@src/assets/icons/fullscreen.svg?react";
import FSBackIcon from "@src/assets/icons/exit_lesson.svg?react";
import FSExitIcon from "@src/assets/icons/fullscreen_exit.svg?react";
import IconPDF from "@src/assets/icons/content_progress_pdf.svg?react";
import IconAudio from "@src/assets/icons/content_progress_audio.svg?react";
import IconSlide from "@src/assets/icons/content_progress_slide.svg?react";
import IconVideo from "@src/assets/icons/content_progress_video.svg?react";
import IconForm from "@src/assets/icons/content_progress_feedback.svg?react";
import { Content, ContentType, Lesson } from "../../../types";
import { useFullScreen } from "@hooks/useFullScreen";
import { useMediaQuery } from "react-responsive";
import ExitModal from "../exit-modal/ExitModal";
import { useCourseContent } from "@hooks/useCourseContent";
import { useNextContent } from "../useNextContent";
import { useMarkLessonComplete, useAnalytics, Events, useUser } from "@hooks";
import { usePresentationContext } from "@hooks/usePresentationContext";
import { isHomeEDApp } from "@src/constants";
import TranslationBadge from "@src/components/translation-provider/TranslationBadge";
import { logger } from '../../../utils/logger';

type Props = {
  content: Content;
  lesson?: Lesson;
  goBack?: () => void;
  children: (isFullScreen: boolean) => React.ReactNode;
  isMaterial?: boolean;
  isFullscreen?: boolean;
};

const getContentIcon = (type: ContentType) => {
  switch (type) {
    case ContentType.PDF: {
      return IconPDF;
    }
    case ContentType.AUDIO: {
      return IconAudio;
    }
    case ContentType.SLIDE:
    case ContentType.REVEALSLIDE:
    case ContentType.ZIPSLIDE: {
      return IconSlide;
    }
    case ContentType.VIDEO: {
      return IconVideo;
    }
    case ContentType.FORM: {
      return IconForm;
    }
    default: {
      return null;
    }
  }
};

function FullScreenWrapper(props: Props) {
  const {
    children,
    content,
    lesson,
    isMaterial = false,
    goBack,
    isFullscreen,
  } = props;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const topbar = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const { open, close, fullScreen } = useFullScreen(wrapperRef);
  const [isHovered, setIsHovered] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [progressWidth, setProgressWidth] = useState(0);
  const { trackEvent } = useAnalytics();
  const [user] = useUser();
  const { setIsPresenting } = usePresentationContext();
  
  // Set presentation state immediately when component mounts
  useEffect(() => {
    logger.info('ProgramFullScreenWrapper mounted - setting presentation mode to TRUE');
    setIsPresenting(true);
    
    return () => {
      logger.info('ProgramFullScreenWrapper unmounted - ending presentation');
      setIsPresenting(false);
    };
  }, [setIsPresenting]); // Include setIsPresenting in the dependency array
  
  // Check if user is from Australia
  const isAUuser = useMemo(() => {
    return user?.id?.toLowerCase().startsWith("au");
  }, [user?.id]);

  const isMobile = useMediaQuery({ maxWidth: 915 });

  const {
    section,
    startedAlternateLesson,
    currentUnit,
    currentHeaUnit,
    program,
    isPreview,
    setLastContent,
    accountLocked,
    currentStudyplan,
    showExitTicket,
    isViewExitTicket,
    isParentApp,
    pageNumber,
    completeModal,
    setCompleteModal,
    isStudyGuide,
    isStudentQuiz,
    studentQuiz,
  } = useCourseContent();
  const { hasNextContent, hasPrevContent } = useNextContent();

  const onMarkLessonCompleted = useCallback(() => {
    setCompleteModal(false);
    setIsComplete(true);
  }, [setCompleteModal]);
  const {
    lessonNotStarted,
    heUpdateLessonInProgress,
    loading: isMarkLessonCompleteLoading,
  } = useMarkLessonComplete(
    onMarkLessonCompleted,
    goBack,
    startedAlternateLesson ? section : undefined
  );

  const enterFullScreen = useCallback(() => {
    open();
  }, [open]);

  const exitFullScreen = useCallback(() => {
    close();
  }, [close]);

  useEffect(() => {
    if (isMaterial) {
      if (isFullscreen) {
        open();
      } else {
        close();
      }
    }
  }, [isMaterial, isFullscreen, open, close]);

  const handleEndLesson = useCallback(() => {
    if (!hasNextContent && content.lastInLesson) {
      if (isComplete && goBack) {
      } else {
        if (isHomeEDApp && accountLocked && goBack) {
          goBack();
        } else if (!completeModal) {
          setCompleteModal(true);
        }
      }
      trackEvent(
        startedAlternateLesson
          ? Events.actions.exitSection
          : Events.actions.exitLesson,
        {
          unit_name: isHomeEDApp ? currentHeaUnit?.name : currentUnit?.name,
          lesson_name: lesson?.name,
          lesson_number: lesson?.number,
          study_plan_id: currentStudyplan?.id,
          study_plan_name: currentStudyplan?.name,
          course_name: program?.name,
          section_name: section?.name,
          section_number: section?.position,
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
        }
      );
    } else if (goBack) {
      if (
        (hasPrevContent || pageNumber > 1) &&
        !isViewExitTicket &&
        !isParentApp &&
        !isHomeEDApp &&
        !startedAlternateLesson
      ) {
        setShowExitModal(true);
      } else {
        goBack();
      }
    }
  }, [
    hasNextContent,
    content.lastInLesson,
    goBack,
    isComplete,
    trackEvent,
    startedAlternateLesson,
    currentHeaUnit?.name,
    currentUnit?.name,
    lesson?.name,
    lesson?.number,
    currentStudyplan?.id,
    currentStudyplan?.name,
    program?.name,
    section?.name,
    section?.position,
    accountLocked,
    hasPrevContent,
    pageNumber,
    isViewExitTicket,
    isParentApp,
    completeModal,
    setCompleteModal,
    user,
  ]);

  const handleNotStartedLesson = useCallback(async () => {
    if (currentUnit && lesson) {
      await lessonNotStarted(currentUnit!.id, lesson?.id);
    }
  }, [currentUnit, lesson, lessonNotStarted]);

  const onSave = useCallback(async () => {
    if (
      isHomeEDApp &&
      currentHeaUnit?.id &&
      lesson?.id &&
      currentStudyplan?.id
    ) {
      await heUpdateLessonInProgress(
        currentHeaUnit?.id,
        lesson?.id,
        currentStudyplan?.id
      );
    }
    setShowExitModal(false);
    if (goBack) {
      goBack();
    }
    trackEvent(
      startedAlternateLesson
        ? Events.actions.exitSection
        : Events.actions.exitLesson,
      {
        unit_name: isHomeEDApp ? currentHeaUnit?.name : currentUnit?.name,
        lesson_name: lesson?.name,
        lesson_number: lesson?.number,
        study_plan_id: currentStudyplan?.id,
        study_plan_name: currentStudyplan?.name,
        course_name: program?.name,
        section_name: section?.name,
        section_number: section?.position,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      }
    );
  }, [
    currentHeaUnit?.id,
    currentHeaUnit?.name,
    currentStudyplan?.id,
    currentStudyplan?.name,
    currentUnit?.name,
    goBack,
    heUpdateLessonInProgress,
    lesson?.id,
    lesson?.name,
    lesson?.number,
    program?.name,
    section?.name,
    section?.position,
    startedAlternateLesson,
    trackEvent,
    user,
  ]);

  const onNoSave = useCallback(async () => {
    await handleNotStartedLesson();
    setShowExitModal(false);
    if (goBack) {
      goBack();
    }
    trackEvent(
      startedAlternateLesson
        ? Events.actions.exitSection
        : Events.actions.exitLesson,
      {
        unit_name: isHomeEDApp ? currentHeaUnit?.name : currentUnit?.name,
        lesson_name: lesson?.name,
        lesson_number: lesson?.number,
        study_plan_id: currentStudyplan?.id,
        study_plan_name: currentStudyplan?.name,
        course_name: program?.name,
        section_name: section?.name,
        section_number: section?.position,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      }
    );
  }, [
    currentHeaUnit?.name,
    currentStudyplan?.id,
    currentStudyplan?.name,
    currentUnit?.name,
    goBack,
    handleNotStartedLesson,
    lesson?.name,
    lesson?.number,
    program?.name,
    section?.name,
    section?.position,
    startedAlternateLesson,
    trackEvent,
    user,
  ]);

  useEffect(() => {
    const modules = [...(lesson?.modules || [])];
    const index = modules.findIndex((m) => m?.id === content?.id);
    const studyIndex = currentUnit?.studyGuides?.findIndex(
      (m) => m?.id === content?.id
    );
    const studentQuizIndex = studentQuiz?.contents?.findIndex(
      (m) => m?.id === content?.id
    );
    if (
      (index === 0 || studyIndex === 0 || studentQuizIndex === 0) &&
      !isPreview &&
      !startedAlternateLesson
    ) {
      enterFullScreen();
    }
  }, [
    content,
    lesson,
    enterFullScreen,
    isPreview,
    startedAlternateLesson,
    currentUnit,
    studentQuiz,
  ]);

  useEffect(() => {
    let timeout: any;

    const displayOpa = () => {
      if (topbar.current) {
        topbar.current.style.opacity = "1";
      }
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        if (topbar.current && document.fullscreenElement) {
          topbar.current.style.opacity = "0";
        }
      }, 5000);
    };
    let refWrapper: any = null;
    if (wrapperRef.current) {
      refWrapper = wrapperRef.current;
      refWrapper.addEventListener("mousemove", displayOpa);
    }

    return () => {
      if (refWrapper) {
        refWrapper.removeEventListener("mousemove", displayOpa);
      }
    };
  }, [wrapperRef]);

  const modules = useMemo(() => {
    // StudyGuide Modules
    if (isStudyGuide && currentUnit?.studyGuides) {
      return currentUnit.studyGuides;
    }
    // Quiz Modules
    if (isStudentQuiz && studentQuiz?.contents) {
      const normalModules = studentQuiz?.contents.filter(
        (content) => content.type !== ContentType.FORM
      );
      const quizModule = studentQuiz?.contents.filter(
        (content) => content.type === ContentType.FORM
      );
      const quizModules = [...normalModules, quizModule[0]];
      return quizModules;
    }
    // Alternate Lesson
    if (section && section.modules?.find((item) => item?.id === content.id)) {
      return section.modules;
    }
    // Main Lesson
    if (lesson && lesson.modules?.find((item) => item?.id === content.id)) {
      const preExitTicket = lesson.modules.find(
        (module) =>
          module.name.toLowerCase().includes("exit ticket") &&
          module.type === ContentType.SLIDE
      );
      const exitTicket = lesson.modules.find(
        (module) =>
          module.name.toLowerCase().includes("exit ticket") &&
          module.type === ContentType.FORM
      );
      if (preExitTicket && exitTicket) {
        return lesson.modules.filter(
          (module) => module.lastInLesson !== true && module !== preExitTicket
        ); // Remove "Lesson Complete" and "PreExitTicket" module on Progress
      }
      return lesson.modules.filter((module) => module.lastInLesson !== true); // Remove Lesson Complete module on Progress
    } else {
      return [];
    }
  }, [
    content,
    lesson,
    section,
    isStudyGuide,
    currentUnit,
    isStudentQuiz,
    studentQuiz,
  ]);
  // check which one is class presentation
  const moduleIndex = useMemo(() => {
    // StudyGuide Modules
    if (isStudyGuide && currentUnit?.studyGuides) {
      return currentUnit.studyGuides.findIndex((m) => m?.id === content?.id);
    }
    // Quiz Modules
    if (isStudentQuiz && studentQuiz?.contents) {
      return studentQuiz?.contents.findIndex((m) => m?.id === content?.id);
    }
    // Alternate Lesson
    if (section && section.modules?.find((item) => item?.id === content.id)) {
      return section.modules.findIndex((m) => m?.id === content?.id);
    }
    // Main Lesson
    if (lesson && lesson.modules?.find((item) => item?.id === content.id)) {
      const progressModules = lesson.modules.filter(
        (module) => module.lastInLesson !== true
      );
      const lessonCompleteModule = lesson.modules.find(
        (module) => module.lastInLesson === true
      );
      if (lessonCompleteModule && lessonCompleteModule.id === content.id) {
        return lesson.modules.findIndex(
          (module) => module === lessonCompleteModule
        );
      }
      return progressModules.findIndex((m) => m?.id === content?.id);
    }
    return -1;
  }, [
    content,
    lesson,
    section,
    isStudyGuide,
    currentUnit,
    isStudentQuiz,
    studentQuiz,
  ]);

  const survey = useMemo(() => {
    // Exit Ticket
    if (content?.type === ContentType.FORM && showExitTicket) {
      return true;
    } else {
      return false;
    }
  }, [content, showExitTicket]);

  useEffect(() => {
    // Alternate Lesson
    if (
      startedAlternateLesson &&
      section &&
      section.modules?.find((item) => item?.id === content.id) &&
      progressRef.current
    ) {
      var contentIndex = modules.findIndex((m) => m?.id === content?.id);
      if (contentIndex > 0) {
        if (contentIndex < modules.length - 1) {
          setProgressWidth(
            ((progressRef.current.clientWidth - 50) * contentIndex) /
              (modules.length - 1)
          );
          setLastContent(false);
        } else {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        }
      } else {
        if (modules.length === 1) {
          setProgressWidth(progressRef.current.clientWidth + 20);
          setLastContent(true);
        } else {
          setProgressWidth(0);
          setLastContent(false);
        }
      }
    } // Main Lesson
    else if (
      lesson &&
      lesson.modules?.find((item) => item?.id === content.id) &&
      progressRef.current
    ) {
      var lessonmodules = lesson.modules.filter(
        (module) => module.lastInLesson !== true
      );
      const preExitTicket = lessonmodules.find(
        (module) =>
          module.name.toLowerCase().includes("exit ticket") &&
          module.type === ContentType.SLIDE
      );
      const exitTicket = lessonmodules.find(
        (module) =>
          module.name.toLowerCase().includes("exit ticket") &&
          module.type === ContentType.FORM
      );
      const lessonCompleteModule = lesson.modules.find(
        (module) => module.lastInLesson === true
      );

      var moduleIndex = modules.findIndex((m) => m?.id === content?.id);
      if (preExitTicket && exitTicket && preExitTicket.id === content.id) {
        moduleIndex = modules.findIndex((m) => m?.id === exitTicket.id);
      } else if (
        lessonCompleteModule &&
        lessonCompleteModule.id === content.id
      ) {
        moduleIndex = lesson.modules.findIndex(
          (m) => m.id === lessonCompleteModule.id
        );
      }
      if (moduleIndex > 0) {
        if (moduleIndex < modules.length - 1) {
          setProgressWidth(
            ((progressRef.current.clientWidth - 50) * moduleIndex) /
              (modules.length - 1)
          );
          setLastContent(false);
        } else {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        }
      } else {
        if (modules.length === 1) {
          setProgressWidth(progressRef.current.clientWidth + 20);
          setLastContent(true);
        } else {
          setProgressWidth(0);
          setLastContent(false);
        }
      }
    } // Study Guide
    else if (
      isStudyGuide &&
      currentUnit?.studyGuides &&
      currentUnit?.studyGuides.find((item) => item?.id === content.id) &&
      progressRef.current
    ) {
      var studyIndex = modules.findIndex((m) => m?.id === content?.id);
      if (studyIndex > 0) {
        if (studyIndex < modules.length - 1) {
          setProgressWidth(
            ((progressRef.current.clientWidth - 50) * studyIndex) /
              (modules.length - 1)
          );
          setLastContent(false);
        } else {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        }
      } else {
        if (modules.length === 1) {
          setProgressWidth(progressRef.current.clientWidth + 20);
          setLastContent(true);
        } else {
          setProgressWidth(0);
          setLastContent(false);
        }
      }
    } // Quiz Progress
    else if (
      isStudentQuiz &&
      studentQuiz?.contents &&
      studentQuiz?.contents.find((item) => item?.id === content.id) &&
      progressRef.current
    ) {
      var quizIndex = modules.findIndex((m) => m?.id === content?.id);
      if (quizIndex > 0) {
        if (quizIndex < modules.length - 1) {
          setProgressWidth(
            ((progressRef.current.clientWidth - 50) * quizIndex) /
              (modules.length - 1)
          );
          setLastContent(false);
        } else {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        }
      } else {
        if (modules.length === 1) {
          setProgressWidth(progressRef.current.clientWidth + 20);
          setLastContent(true);
        } else {
          setProgressWidth(0);
          setLastContent(false);
        }
      }
    }
  }, [
    content,
    startedAlternateLesson,
    lesson,
    section,
    modules,
    isStudyGuide,
    currentUnit,
    isStudentQuiz,
    studentQuiz,
    setLastContent,
  ]);

  const fullScreenButtonBlock = useMemo(() => {
    const percent =
      modules.length === 1 ? 100 : ((moduleIndex + 1) / modules.length) * 100;
    const isClassPresentation = moduleIndex > -1;

    return (
      <div
        ref={topbar}
        className={`${
          fullScreen || isClassPresentation
            ? styles.fullScreenActions
            : styles.actions
        } ${fullScreen || isClassPresentation ? styles.noPadding : ""}`}
        style={{
          backgroundColor: survey && !isViewExitTicket ? "white" : "black",
        }}
      >
        {fullScreen || isClassPresentation ? (
          <button
            onClick={handleEndLesson}
            className={`${styles.button} ${
              fullScreen || isClassPresentation ? styles.buttonFull : ""
            }`}
            type={"button"}
            onMouseOver={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className={styles.backIconWrapper}>
              <FSBackIcon />
            </div>
          </button>
        ) : null}
        <div
          className={styles.backTipView}
          style={{ display: isHovered ? "flex" : "none" }}
        >
          <div className={styles.arrowUp}></div>
          <div className={styles.tipTextView}>
            <div className={styles.tipTitle}>Exit Lesson</div>
          </div>
        </div>
        {isViewExitTicket ? (
          <>
            <div className={styles.exitTicketNameView}>
              <strong>{currentStudyplan?.name}'s</strong>&nbsp;Exit Ticket
              Results
            </div>
          </>
        ) : (
          ((fullScreen && isClassPresentation) || isClassPresentation) && (
            <div ref={progressRef} className={styles.progressBackBar}>
              <div
                className={styles.progressBar}
                style={{
                  width: `${progressWidth}px`,
                  borderRadius: percent === 100 ? "3px" : "",
                }}
              ></div>
              <div className={styles.iconView}>
                {modules.map((item: any) => {
                  if (item.type === ContentType.PARENT) {
                    return <div key={item.id}>PS</div>;
                  } else {
                    const Icon = getContentIcon(item.type);
                    return (
                      <div key={item.id}>
                        {Icon && <Icon className={styles.moduleIcon} />}
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          )
        )}
        {!isViewExitTicket ? (
          <>
            {/* Translation Badge for full screen mode - hidden for Australian users */}
            {(fullScreen || isClassPresentation) && !isAUuser && (
              <TranslationBadge type="presentation" />
            )}
            
            {fullScreen ? (
              <button
                onClick={exitFullScreen}
                className={`${styles.button} ${
                  fullScreen ? styles.buttonFull : ""
                }`}
                type={"button"}
              >
                <div className={styles.iconWrapper}>
                  <FSExitIcon />
                </div>
                {!isMobile && (
                  <span className={styles.textWhite}>Exit full screen</span>
                )}
              </button>
            ) : (
              <button
                onClick={enterFullScreen}
                className={`${styles.button} ${
                  fullScreen ? styles.buttonFull : ""
                }`}
                type={"button"}
              >
                <div className={styles.iconWrapper}>
                  <FSIcon />
                </div>
                {!isMobile && (
                  <span className={styles.textWhite}>Open full screen</span>
                )}
              </button>
            )}
          </>
        ) : null}
      </div>
    );
  }, [
    enterFullScreen,
    exitFullScreen,
    handleEndLesson,
    fullScreen,
    moduleIndex,
    isMobile,
    modules,
    isHovered,
    survey,
    progressWidth,
    currentStudyplan?.name,
    isViewExitTicket,
  ]);

  return (
    <div
      id="fswrapper"
      className={styles.wrapper}
      ref={wrapperRef}
      style={{
        backgroundColor:
          (survey && !isViewExitTicket) || isMaterial ? "white" : "black",
      }}
    >
      {isMaterial ? null : fullScreenButtonBlock}
      <div
        className={`${styles.content} ${
          fullScreen
            ? `${styles.isFull} ${isMobile ? styles.isMobileFull : ""}`
            : ""
        }`}
        style={{ marginTop: isMaterial ? 0 : 75 }}
      >
        {children(fullScreen)}
      </div>
      {showExitModal && (
        <ExitModal
          open={showExitModal}
          onSave={onSave}
          onNoSave={onNoSave}
          onClose={() => setShowExitModal(false)}
          loading={isMarkLessonCompleteLoading}
        />
      )}
    </div>
  );
}

export default FullScreenWrapper;
